import type { ComputedRef } from 'vue'
import type { CustomerListItem } from '@entities/customer'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'

import { useDayjs } from '@shared/lib/format'

import { useCustomersStore } from './store'
import { getCustomerUrl, getKidUrl } from './customer-links'
import { CUSTOMER_EMPTY_CELL } from './customer-options'
import { useCustomerPrivileges } from '../customer-privileges.hook'

interface TableCustomersReturn {
  customers: ComputedRef<CustomerListItem[]>
  isLoading: ComputedRef<boolean>
  hasCustomers: ComputedRef<boolean>
  hasNext: ComputedRef<boolean>
  hasPrev: ComputedRef<boolean>
  perPage: ComputedRef<number>
  count: ComputedRef<number>
  canViewContacts: ComputedRef<boolean>
  getStatus: (customer: CustomerListItem) => CustomerStatus
  getCreatedAt: (customer: CustomerListItem) => string
  getCustomerUrl: (customerId: number) => string
  getKidUrl: (kidId: number) => string
  goPrev: () => Promise<void>
  goNext: () => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableCustomers = (): TableCustomersReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useCustomersStore()
  const dayjs = useDayjs()
  const { canViewCustomers } = useCustomerPrivileges()

  const customers = computed<CustomerListItem[]>(() => store.getCustomers)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasCustomers = computed<boolean>(() => store.hasCustomers)
  const hasNext = computed<boolean>(() => store.hasNextPage)
  const hasPrev = computed<boolean>(() => store.hasPrevPage)
  const perPage = computed<number>(() => store.getMeta?.per_page ?? store.getPerPage)
  const count = computed<number>(() => store.getCustomers.length)

  const getStatus = (customer: CustomerListItem): CustomerStatus => customer.status as CustomerStatus

  const getCreatedAt = (customer: CustomerListItem): string => {
    if (!customer.created_at) return CUSTOMER_EMPTY_CELL

    const createdAt = dayjs(customer.created_at)

    return createdAt.isValid() ? createdAt.format(DateFormat.KanbanDate) : CUSTOMER_EMPTY_CELL
  }

  /** Simple pagination has no total, so pages are stepped from the current one. */
  const goToPage = async (page: number): Promise<void> => {
    store.setCurrentPage(page)
    await router.push({
      query: {
        ...route.query,
        page: page > 1 ? String(page) : undefined,
      },
    })
  }

  const goPrev = (): Promise<void> => goToPage(Math.max(store.getCurrentPage - 1, 1))
  const goNext = (): Promise<void> => goToPage(store.getCurrentPage + 1)

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    customers,
    isLoading,
    hasCustomers,
    hasNext,
    hasPrev,
    perPage,
    count,
    canViewContacts: canViewCustomers,
    getStatus,
    getCreatedAt,
    getCustomerUrl,
    getKidUrl,
    goPrev,
    goNext,
    setChangePerPage,
  }
}
